import { Box, List, Divider, ListItemButton, ListItemText, Typography, TextField, Stack, ListItemIcon, styled, Button, Grid, 
  OutlinedInput, Card, IconButton, Input, FormGroup, Switch, FormControlLabel, Paper, Tooltip, FormHelperText } from "@mui/material";
import StarIcon from "@mui/icons-material/Star"
import RemoveIcon from '@mui/icons-material/Remove';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { ChangeEvent, FC, useCallback, useMemo, useState, useEffect, createContext, useContext } from "react";
import { AddSubsetMasterTablesInput, DatabaseResponseDto, SubsetMasterTableInput, SubsetMasterTableResponseDto, TableSchemaAndNameDto, 
  TableWithRefsResponseDto, UpdateDatabaseSubsetLimitInput } from "~/gen/api/encrypt";
import { Content } from "../content/Content";
import { OdResourcePageProps } from "~/types/resource";
import { useDatabaseSelectCard, useEncryptableDatabasesSource } from "../databases/DatabaseSelectCard";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import { decodeHashid, genUrlWithHashid } from "~/utils/hashid";
import { ShowBase, useGetOne, useSaveContext, useShowContext } from "ra-core";
import { RecPatch } from "~/dataProvider/tools";
import Create from "~/controller/Create";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { MANAGE_DATABASE_OPERATIONS } from "~/constants";
import { disabledColor } from "~/utils/a11y";
import { nonSqlDbTypes } from "./defs";
import { EncryptionMappingResponseDto } from "~/gen/api/governance";
import { Redirect } from "react-router-dom";


const DbSubsettingDetail: FC<OdResourcePageProps & { id?: string}> = (props) => {
  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '') 
  const dispatch = useDispatch()

  const handleSwitchDatabase = useCallback((db: DatabaseResponseDto) => {
    dispatch(push(
      genUrlWithHashid(`/encryption/subsetting/mastertables/$0/show`, db.databaseId || 0, db.name)))
  }, [dispatch])

  return (
    <DbSubsettingDetailBody onSwitchDatabase={handleSwitchDatabase} {...{id}} {...rest} />
  )
}

interface DbSubsettingDetailBodyProps extends OdResourcePageProps {
  onSwitchDatabase: (x: DatabaseResponseDto) => void;
  id: number;
}

const DbSubsettingDetailBody: FC<DbSubsettingDetailBodyProps> = ({
  id,
  onSwitchDatabase,
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const { data: dbTables } = useGetOne<{data:TableWithRefsResponseDto[]}&RecPatch>('subset-tablerefs', id)
  const candidates = useMemo(() => { 
    return getMasterTableCandidates(dbTables?.data ?? [])
  }, [dbTables?.data])
  
  //@ts-ignore
  const dbs = useEncryptableDatabasesSource(true)
  const useDataSource = () => useMemo(() => dbs.filter(db => !nonSqlDbTypes.has((db as EncryptionMappingResponseDto).databaseType!)), [dbs])
  const DatabaseSelectCard = useDatabaseSelectCard(useDataSource)

  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      onSwitchDatabase(chosenDb)
    }
  }, [onSwitchDatabase])

  const dbLimitHandler = useDefaultSavedHandlers("Save Database Subset Limit")
  const masterTableHandler = useDefaultSavedHandlers("Save Master Tables Setting")

  const currDb = dbs.find(db => db.databaseId === id) as DatabaseResponseDto|undefined
  if (dbs.length > 0 && (!currDb || nonSqlDbTypes.has(currDb.databaseType!))) {
    return <Redirect to="/encryption/dashboard" />
  }
  
  return (
    <Content 
      pageName="Database Subsetting Config" 
      resourceName="Subsetting Config" 
      breadcrumbs={options?.breadcrumbs}
    >

      <Grid container spacing={4} {...(!hasWritePermit && { sx: { '& input.Mui-disabled': disabledColor }})}>
        <Grid item md={6} xs={12} >
          <DatabaseSelectCard preSelectDbId={id} onChange={handleChange} />
        </Grid>
        <Grid item xs={12} >
          <Card>
            <Create resource="subset-database-default-limit" basePath="" {...dbLimitHandler}>
              <DatabaseSubsetLimtEditor databaseId={id} />
            </Create>
            <Divider />
            <Create resource="encryption/subsetting/mastertables" basePath="" {...masterTableHandler}>
              <ShowBase resource="encryption/subsetting/mastertables" id={id} basePath="">
                <MasterTablesEditor databaseId={id} candidate={candidates} />
              </ShowBase>
            </Create>
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default DbSubsettingDetail;


const MasterTablesEditor : FC<{candidate: CalculatedCandidates, databaseId: number}> = ({
  candidate,
  databaseId,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const ctrls = useMasterTableSettingController(candidate)
  const { refetch, loading } = useShowContext()
  const { saving } = useSaveContext()
  const save = useSimpleSaveAction()
  const handleSave = () => {
    if (!ctrls.hasEdits() || saving || loading) { return }
    const payload: AddSubsetMasterTablesInput = {
      databaseId,
      masterTables: ctrls.gatherValues().map(x => (x.databaseId = databaseId, x)),
    }
    save(payload, {
      onSuccess: () => {
        refetch && refetch()
      },
    })
  }
  const [showMore, setShowMore] = useState(false)
  return (
    <MasterTableSettingContext.Provider value={ctrls}>
      <Stack sx={{ px: 3, py: 2 }} direction="row" alignItems="center">
        <Typography variant="subtitle2">
          Master Tables Setting
        </Typography>
        <FormGroup sx={{ ml: 8, mr: 'auto' }}>
          <FormControlLabel sx={{ mr: 0 }} 
            control={<Switch size="small" value={showMore} onChange={(e, checked) => setShowMore(checked)} />} 
            label={
              <Typography variant="caption" color="info">Include Standalone Tables</Typography>} />
          <FormHelperText sx={{ color: 'InactiveCaptionText' }}>
            A table without any relationship with other tables is considered a standalone table.
          </FormHelperText>
        </FormGroup>
        {hasWritePermit && ctrls.hasEdits() &&
          <>
            {ctrls.hasEdits(true) &&
              <Button color="primary" variant="outlined" size="small"
                onClick={ctrls.reset}
              >
                cancel
              </Button>}
            <Button color="primary" variant="contained" size="small" sx={{ ml: 2 }} 
              disabled={saving || loading}
              onClick={handleSave}
            >
              Save Change
            </Button>
          </>}
      </Stack>
      <Divider />
      <TableUnitGroupEditor candidate={candidate} showMore={showMore} />
    </MasterTableSettingContext.Provider>
  )
}

const MasterTableSettingContext = createContext({} as MasterTableSettingControllerProps)
const useMasterTableSettingController = (candidate: CalculatedCandidates):MasterTableSettingControllerProps => {
  const { record: prevSettings } = useShowContext<{data:SubsetMasterTableResponseDto[]}&RecPatch>()

  const [states, setStates] = useState({
    displayIds: [] as string[],
    origin: {} as Record<string, TableInputUnit>,
    originMissing: [] as SubsetMasterTableResponseDto[],
    added: {} as Record<string, TableInputUnit>,
    removed: new Set<string>(),
  })

  useEffect(() => {
    const resetted = { 
      displayIds: [] as string[],
      origin: {} as Record<string, TableInputUnit>, 
      originMissing: [] as SubsetMasterTableResponseDto[],
      added: {} as Record<string, TableInputUnit>, 
      removed: new Set<string>() 
    }
    if (!prevSettings || prevSettings.data?.length === 0 || candidate.allCandidates.length === 0) {
      setStates(resetted)
      return
    }
    (prevSettings.data || []).reduce((acc, x) => {
      const id = `${x.schemaName}!${x.tableName}`
      if (candidate.allCandidates.some(c => c.id === id)) {
        acc[id] = {...x, id} as any
        resetted.displayIds.push(id)
      } else {
        resetted.originMissing.push(x)
      }
      return acc
    }, resetted.origin)
    resetted.displayIds.sort()
    
    setStates(resetted)
  }, [prevSettings, candidate])

  const valueLens = {
    getValue: (id: string): TableInputUnit|undefined => {
      if (states.removed.has(id)) { return undefined }
      return states.added[id] ?? states.origin[id]
    },
    setValue: (id: string, x: TableInputUnit|undefined) => {
      setStates(v => {
        let dirty = false
        if (!x) {
          if (!v.removed.has(id)) {
            dirty = true
            delete v.added[id]
            v.removed.add(id)
          }
        } else {
          dirty = true
          if (v.removed.has(id)) {
            v.removed.delete(id)
          } else {
            v.added[id] = x
          }
        }
        const displayIds = !dirty  ? [] : 
          [...new Set<string>([...Object.keys(states.origin), ...Object.keys(states.added)])].sort()
        return dirty ? {...v, displayIds} : v
      })
    },
    isAdded: (id: string) => { return !!states.added[id] && !states.origin[id] },
    isEditted: (id: string) => { return !!states.added[id] && !!states.origin[id] },
    isDeleted: (id: string) => { return states.removed.has(id) },
    lookupOrigin: (id: string) => { return states.origin[id] },
  }
  const gatherValues = () => {
    const values = {...(states.origin), ...(states.added)}
    for(const id of states.removed) {
      delete values[id]
    }
    return Object.values(values)
  }
  const hasEdits = (noInherited?:boolean) => {
    return states.removed.size > 0 || Object.keys(states.added).length > 0 || (noInherited ? false : states.originMissing.length > 0)
  }
  const isMasterTable = (id:string) => !states.removed.has(id) && (!!states.origin[id] || !!states.added[id])
  const reset = () => {
    setStates(v => ({...v,
      added: {},
      removed: new Set<string>(),
      displayIds: Object.keys(v.origin).sort(),
    }))
  }

  return { displayIds: states.displayIds, valueLens, originMissing: states.originMissing, gatherValues, reset, hasEdits, isMasterTable }
}

interface MasterTableSettingControllerProps {
  displayIds: string[];
  originMissing: SubsetMasterTableResponseDto[],
  valueLens: {
    getValue: (id: string) => TableInputUnit | undefined;
    setValue: (id: string, x: TableInputUnit | undefined) => void;
    isAdded: (id: string) => boolean;
    isEditted: (id: string) => boolean;
    isDeleted: (id: string) => boolean;
    lookupOrigin: (id: string) => TableInputUnit|undefined;
  };
  gatherValues: () => TableInputUnit[];
  reset: () => void;
  hasEdits: (noInherited?:boolean) => boolean;
  isMasterTable: (id: string) => boolean;
}

const DatabaseSubsetLimtEditor : FC<{databaseId: number}> = ({ databaseId }) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const { data: currDb, loading } = useGetOne<DatabaseResponseDto&RecPatch>('db-esc-status', databaseId)
  const save = useSimpleSaveAction()

  const [states, setStates] = useState({
    subsetLimit: currDb?.subsetLimit ?? 0,
    idVersion: currDb?.databaseId,
  })
  useEffect(() => {
    setStates({
      subsetLimit: currDb?.subsetLimit ?? 0,
      idVersion: currDb?.databaseId,
    })
  }, [currDb?.databaseId, currDb?.subsetLimit])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value)
    if (isNaN(value) || value < 0 || !Number.isInteger(value)) { return }
    setStates(v => ({...v, subsetLimit: value }))
  }

  const handleSave = () => {
    if (!hasWritePermit || !currDb || states.subsetLimit === currDb.subsetLimit) { return }
    const payload : UpdateDatabaseSubsetLimitInput = {
      databaseId,
      subsetLimit: states.subsetLimit,
    }
    save(payload, {
      onSuccess: () => {
        currDb.subsetLimit = payload.subsetLimit
      }
    })
  }
  return (
    <Stack direction="row" sx={{ py: 1, px: 3, }} alignItems="center">
      <Box sx={{ width: 500 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography id="subset-threshold-lable" variant="subtitle2">Subset Threshold</Typography>
          <OutlinedInput size="small" value={states.subsetLimit} onChange={handleChange} sx={{ mr: 4 }} disabled={!hasWritePermit} {...{inputProps: { "aria-labelledby": 'subset-threshold-lable' }}} />
        </Stack>
        <Typography variant="caption" color="InactiveCaptionText" sx={{ lineHeight: '12px' }}>
          If the total number of rows in a table is less than the Subset Threshold
          , by default all rows in that table will be copied over, instead of taking a subset of rows from that table.
        </Typography>
      </Box>
      {hasWritePermit && !loading && states.idVersion === currDb?.databaseId && states.subsetLimit !== currDb?.subsetLimit && 
        <>
          <Button color="primary" variant="outlined" size="small" sx={{ ml: 'auto' }} 
            onClick={() => setStates(v => ({...v, subsetLimit: currDb?.subsetLimit ?? 0}))}
          >
            cancel
          </Button>
          <Button color="primary" variant="contained" size="small" sx={{ ml: 2 }} 
            disabled={states.subsetLimit === currDb?.subsetLimit}
            onClick={handleSave}
          >
            Save Change
          </Button>
        </>
      }
    </Stack>
  )
}

const TableUnitGroupEditor: FC<{ candidate: CalculatedCandidates, showMore: boolean}> = ({
  candidate,
  showMore,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATABASE_OPERATIONS])
  const [states, setStates] = useState({
    filterTerm: '',
    rowCount: '' as number|'',
    activeOption: null as TableUnit|null,
  })
  const { displayIds, originMissing, valueLens, isMasterTable } = useContext(MasterTableSettingContext)

  const handleKeyPress = (c:TableUnit) => (e:React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code !== 'Enter' && e.code !== 'NumpadEnter') { return }
    if (states.rowCount === '') { return }
    e.preventDefault()
    e.stopPropagation()
    valueLens.setValue(c.id, { 
      id: c.id, 
      databaseId: 0, 
      schemaName: c.schema as any, 
      tableName: c.name as any,
      rowCount: states.rowCount,
    })
    setStates(v => ({...v, activeOption:null, rowCount: ''}))
  }
  const handleRowCountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const count = Number(e.target.value)
    if (e.target.value && isNaN(count)) { return }
    setStates(v => ({...v, rowCount: count }))
  }
  return (
    <TableUnitGroupEditorRoot className="u-group">
      <Box className="u-group-main">
        <Box className="header" id="u-group-header">
          <Stack direction="row" alignItems="center">
            <Typography variant="caption" color="InactiveCaptionText">Choose master tables:</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Input placeholder="Filter..." 
              sx={{ '& input': { ml: 0.5, fontSize: '14px' } }}
              startAdornment={<SearchIcon fontSize="small" />}
              value={states.filterTerm}
              onChange={e => setStates(v => ({...v, filterTerm: e.target.value }))}
              {...{inputProps: { "aria-label": 'Tables Filter' }}}
            />
          </Stack>
        </Box>
        <List component="div" role="listbox" aria-labelledby="u-group-header" >
          <Divider role="presentation" />
          {(showMore ? candidate.allCandidates : candidate.recommanded)
            .filter(c => !states.filterTerm || (
              (c.schema && c.schema.toLowerCase().indexOf(states.filterTerm.toLowerCase()) > -1) ||
              (c.name && c.name.toLowerCase().indexOf(states.filterTerm.toLowerCase()) > -1)
            ))
            .map((c, i) => (
            <ListItemButton key={c.id} divider={true} selected={states.activeOption === c} role="option"
              onClick={(e) => {
                if (!hasWritePermit || isMasterTable(c.id)) { return }
                const div = e.currentTarget
                setTimeout(() => div.querySelector('input')?.focus(), 10)
                setStates(v => ({...v, activeOption:c, rowCount: ''}))
              }}
            >
              <ListItemIcon sx={{ minWidth: 24 }}>
                {isMasterTable(c.id) && <StarIcon color={valueLens.isAdded(c.id) ? "success" : "info"} />}
              </ListItemIcon>
              <ListItemText>
                <Stack direction="row" alignItems="center">
                  <Box sx={{ mr: 2 }}>
                    <Typography variant="caption" color="InactiveCaptionText">{c.schema}. </Typography>
                    <Typography variant="body2">{c.name}</Typography>
                  </Box>
                  <Box sx={{ ml: 'auto' }}>
                    {hasWritePermit && states.activeOption === c 
                    ? <Box sx={{ width: 100, }}>
                        <Input id="standard-basic" placeholder="Row Count" size="small" sx={{ '& input': {textAlign:'right'} }} 
                          value={states.rowCount}
                          onChange={handleRowCountChange}
                          onKeyPress={handleKeyPress(c)}
                          onBlur={() => setTimeout(() => setStates(v => ({...v, activeOption:null, rowCount: ''})))}
                        />
                      </Box>
                    : <Box sx={{ '& div': { display: 'inline-block' } }}>
                        <Tooltip title="Total Row Count">
                          <Typography variant="caption" component="div" color="secondary" sx={{ mr: 1 }}>{c.totalRows ?? 'n/a'}</Typography>
                        </Tooltip>
                        <Tooltip title="Upstream Table Count">
                          <Typography variant="caption" component="div" color="info">{c.upstreams ?? 0}</Typography>
                        </Tooltip>
                      </Box>}
                  </Box>
                </Stack>
              </ListItemText>
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Box className="u-group-mtable-setting" >
        <Typography variant="body2" sx={{ mb: 2 }}>Master Tables: </Typography>
        <Stack direction="row" flexWrap="wrap" alignItems="start">
          {originMissing.map((x, i) => <TableUnitEntry id={i+''} missingEntry={x} valueLens={valueLens} candidate={candidate} disabled={!hasWritePermit} />)}
          {displayIds.map(id => <TableUnitEntry id={id} valueLens={valueLens} candidate={candidate} disabled={!hasWritePermit} />)}
        </Stack>
      </Box>
    </TableUnitGroupEditorRoot>
  )
}

const TableUnitEntry : FC<{
  id: string,
  valueLens: MasterTableSettingControllerProps["valueLens"],
  candidate: CalculatedCandidates,
  missingEntry?: SubsetMasterTableResponseDto,
  disabled?: boolean,
}> = ({
  id,
  valueLens,
  candidate,
  missingEntry,
  disabled,
}) => {
  let [schema, tablename] = ['' as string|undefined|null, '' as string|undefined|null]
  let [isDel, isAdd] = [false, false]
  let [handleButtonClick, handleChange] = [() => {}, (e:ChangeEvent<HTMLInputElement>)=> {}]
  let value :TableInputUnit | undefined = undefined
  if (!missingEntry) {
    value = valueLens.getValue(id)
    const originValue = valueLens.lookupOrigin(id)
    const unit : TableUnit = (value ? {} : candidate.allCandidates.find(c => c.id === id)) as any;
    handleButtonClick = () => {
      if (disabled) { return }
      valueLens.setValue(id, isDel ? { ...originValue } as any : undefined)
    }
    handleChange = e => {
      if (disabled) { return }
      const count = !e.target.value ? 0 : Number(e.target.value)
      if (e.target.value && isNaN(count)) { return }
      const newVal : TableInputUnit = 
        value   
        ? {...value, rowCount: count}
        : { id, schemaName: unit.schema as any, tableName: unit.name as any, rowCount: count }
      valueLens.setValue(id, newVal)
    }
    [schema, tablename] = value ? [value.schemaName, value.tableName]
      : [unit?.schema, unit?.name];
    [isDel, isAdd] = [valueLens.isDeleted(id), valueLens.isAdded(id)]
  } else {
    [schema, tablename] = [missingEntry.schemaName, missingEntry.tableName]
    isDel = true
  }

  return (
    <Paper className={isAdd ? 'isAdd' : isDel ? 'isDel' : undefined} >
      <Typography variant="body2" className="mt-name">
        <b>{`${schema}. ${tablename}`}</b>
        {(missingEntry || !disabled) &&
          <Tooltip title={
            missingEntry ? 'This setting is obsolete due to database schema change'
            : isDel ? "Add Back" : "Remove"
          }>
            {missingEntry
            ? <Box sx={{ ml: 'auto' }} role="tooltip"><ErrorOutlineIcon color="warning" fontSize="small" /></Box>
            : <IconButton color="secondary" sx={{ mr: -1, ml: 'auto' }}
                onClick={handleButtonClick}
              >
                {isDel ? <AddIcon /> : <RemoveIcon />}
              </IconButton>}
          </Tooltip>}
      </Typography>
      {!isDel &&
        <Box sx={{ pt: 2, }}>
          <TextField fullWidth size="small" 
            disabled={disabled}
            label="Target Row Count"
            value={value?.rowCount || 0}
            onChange={handleChange}
          />
        </Box>
      }
    </Paper>
  )
}

const TableUnitGroupEditorRoot = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  backgroundColor: theme.palette.background.paper,
  '& div.u-group-main': {
    borderTopLeftRadius: theme.spacing(0.75),
    borderBottomLeftRadius: theme.spacing(0.75),
    borderRight: `1px solid ${theme.palette.divider}`,
    '& div.header': {
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    '& div.MuiList-root': {
      paddingTop: 0,
      paddingBottom: 0,
      minWidth: 300,
      minHeight: 350,
      maxHeight: 840,
      overflowY: 'auto',
    },
  },
  '& div.u-group-mtable-setting': {
    flex: '1 1 auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    '& div.MuiPaper-root': {
      borderRadius: '6px',
      padding: theme.spacing(1.5),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      minWidth: 210,
      border: `1px solid ${theme.palette.divider}`,
      '&.isAdd': { border: `1px solid ${theme.palette.success.main}`, },
      '&.isDel': { border: `1px solid ${theme.palette.error.main}`, 
        '& b': {
          textDecoration: 'line-through',
          color: theme.palette.error.main,
        }
      },

      '& .mt-name': { display: 'flex', alignItems: 'center' },
    },
  },
}))

// Objectives:
// * find table groups, and other individual tables
// * identify master table candidates for each table group, calculate priorities for candidates
// 
// 
// const ls : TableWithRefsResponseDto[] = [
//   { schema: 'dbo', name: '1', refs: [] },
//   { schema: 'dbo', name: '2', refs: [
//     { schema: 'dbo', name: '1', },
//     { schema: 'dbo', name: '4', },
//   ] },
//   { schema: 'dbo', name: '3', refs: [] },
//   { schema: 'dbo', name: '4', refs: [
//     { schema: 'dbo', name: '1', },
//     { schema: 'dbo', name: '5', },
//   ] },
//   { schema: 'dbo', name: '5', refs: [
//     { schema: 'dbo', name: '2', },
//     { schema: 'dbo', name: '3', },
//   ] },
//   { schema: 'dbo', name: '6', refs: [
//     { schema: 'dbo', name: '3', },
//     { schema: 'dbo', name: '6', }
//   ] },
//   { schema: 'dbo', name: '7', refs: [] },
//   { schema: 'dbo', name: '8', refs: [] },
// ]

const getMasterTableCandidates = (ls: TableWithRefsResponseDto[]) : CalculatedCandidates => {
  const result = { recommanded: [] as TableUnit[], allCandidates: [] as TableUnit[] }
  
  const groups = getUnitGroups(ls)
  groups.forEach(g => {
    if (g.candidates.length === 0) {
      const units = Object.values(g.units)
      if (units.length === 1) {
        result.allCandidates.push(Object.values(g.units)[0])
      } else if (units.length > 1) {
        units.sort((a, b) => 
          (a.upstreams ?? 0) === (b.upstreams ?? 0) ? (a.id < b.id ?  -1 : 1)
          : (a.upstreams ?? 0) > (b.upstreams ?? 0) ? -1 : 1)
        if (units[0].upstreams === units[1].upstreams) {
          result.allCandidates.push(...units)
        } else {
          result.recommanded.push(units[0])
        }
      }
    } else {
      result.recommanded.push(...g.candidates)
      result.allCandidates.push(...g.candidates)
    }
  })
  result.recommanded = result.recommanded.sort((a, b) => a.id < b.id ? -1 : 1)
  result.allCandidates = result.allCandidates.sort((a, b) => a.id < b.id ? -1 : 1)
  return result
}

interface CalculatedCandidates { 
  recommanded: TableUnit[], 
  allCandidates: TableUnit[], 
}

const getUnitGroups = (ls : TableWithRefsResponseDto[]) => {
  const allUnits : Record<string, TableUnit> = {}
  // directed graph - flow from Foreign to Primary
  const gF2P = ls.reduce((acc, x) => {
    const key = `${x.schema}!${x.name}`
    allUnits[key] = { id: key, schema: x.schema, name: x.name, totalRows: x.totalRows }
    acc[key] = (x.refs || []).map(r => {
      const u = ({ id: `${r.schema}!${r.name}`, ...r })
      allUnits[u.id] = u
      return u
    })
    return acc
  }, {} as Record<string, TableUnit[]>)
  
  // directed graph - flow from Primary to Foreign
  const gP2F = Object.keys(gF2P).reduce((acc, pId) => {
    const p = allUnits[pId]
    const fs = gF2P[pId] || []
    for(const f of fs) {
      const arr = acc[f.id] || (acc[f.id] = [])
      arr.push(p)
    }
    return acc
  }, {} as Record<string, TableUnit[]>)

  const visited = new Set<string>()
  const unitGroups = Object.keys(allUnits).reduce((acc, id) => {
    if (visited.has(id)) { return acc }
    const group : UnitGroup = { units: {}, travelStack: [], candidates: [], accessableCounts: {}, }
    const visit = (u: TableUnit) => {
      visited.add(u.id)
      group.units[u.id] = u
      const pfOuts = gP2F[u.id] ?? []
      const fpOuts = gF2P[u.id] ?? []
      group.travelStack.push(...pfOuts, ...fpOuts)
      if (pfOuts.length === 0 && fpOuts.length > 0) {
        group.candidates.push(u)
      }
    }
    const start = allUnits[id]
    visit(start)
    
    while (group.travelStack.length > 0) {
      const u = group.travelStack.pop()
      if (u && !visited.has(u.id)) {
        visit(u)
      }
    }

    // calculate accessableCounts for candidates
    group.candidates.forEach(c => {
      const visitedInner = new Set<string>()
      const visitInner = (u:TableUnit) => {
        visitedInner.add(u.id)
        group.travelStack.push(...(gF2P[u.id] ?? []))
        group.accessableCounts[c.id] = (group.accessableCounts[c.id] ?? 0) + 1
      }
      visitInner(c)
      while (group.travelStack.length > 0) {
        const u = group.travelStack.pop()
        if (u && !visitedInner.has(u.id)) {
          visitInner(u)
        }
      }
      c.upstreams = group.accessableCounts[c.id] != null 
        ? group.accessableCounts[c.id] - 1
        : 0
    })
    group.candidates.sort((a, b) => group.accessableCounts[b.id] - group.accessableCounts[a.id])

    acc.push(group)
    return acc
  }, [] as UnitGroup[])

  return unitGroups
}

interface TableUnit extends TableSchemaAndNameDto {
  id: string,
  upstreams?: number,
}

interface TableInputUnit extends SubsetMasterTableInput {
  id: string
}

interface UnitGroup {
  units: Record<string, TableUnit>,
  travelStack: TableUnit[],
  candidates: TableUnit[],
  accessableCounts: Record<string, number>,
}

